import {
  AfterContentInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PuzzleFrameworkService } from '../../puzzle-framework-module/puzzle-framework.service';
import SudokuTile from '../interfaces/sudoku-tile';
import SudokuPuzzleConfig from '../interfaces/sudoku-puzzle-config';
import { PuzzleGameState } from '../../store/puzzle-game.state';
import { PuzzleGameTypesEnum } from '../../puzzle-framework-module/puzzle-game-types.enum';
import { Store } from '@ngxs/store';
import { SetPuzzleID } from '../../store/puzzle-id.actions';
import { SudokuGameState } from '../store/sudoku-game.state';
import { InitSudokuGame, PersistSudokuState } from '../store/sudoku-game.actions';
import { ConsumeJoker, IncMoveCounter, SetFinished, SetGameProgress } from '../../store/puzzle-game.actions';
import {
  PUZZLE_WIN_DIALOG_BUTTONS,
  PuzzleWinDialogService,
} from '../../shared-module/puzzle-win-dialog/puzzle-win-dialog.service';
import { Subscription } from 'rxjs';
import { Button } from '../../shared-module/buttons/button.def';
import { TranslateService } from '@ngx-translate/core';
import { DisableNotesMode, EnableNotesMode } from '../../store/puzzle-notes-mode.actions';
import { PuzzleHelpDialogService } from '../../shared-module/puzzle-help-dialog/puzzle-help-dialog.service';
import { SudokuHelpComponent } from '../help/sudoku-help.component';
import {
  PuzzleSpecialKeys,
  PuzzleKeyboardService,
} from '../../shared-module/puzzle-softkeyboard/puzzle-keyboard.service';
import { SudokuWinScreenComponent } from '../win-screen/sudoku-win-screen.component';
import { SudokuGameModel } from '../store/sudoku-game.model';
import { PuzzleFrameworkEvents } from '../../puzzle-framework-module/puzzle-framework-events.enum';
import { HelperFunctionsService } from '../../shared-module/helpers/helper-functions.service';
import { SudokuKeyboardComponent } from 'projects/tools/src/lib/sudoku-module/keyboard/sudoku-keyboard.component';
import { SudokuKeyboardLayouts } from '../keyboard/sudoku-keyboard.component';
import { ResizeObserver } from '@juggle/resize-observer';
import { PuzzleSettingsModalService } from '../../shared-module/puzzle-settings-modal/puzzle-settings-modal.service';

export enum SudokuBorderTypes {
  TOP,
  LEFT,
  BOTTOM,
  RIGHT,
}

export enum SudokuKeyboardPositions {
  BOTTOM,
  RIGHT,
}

export interface SudokuBoardLayout {
  puzzleScale: number;
  keyboardPosition?: SudokuKeyboardPositions;
  keyboardLayout?: SudokuKeyboardLayouts;
  keyboardSize?: number;
}

@Component({
  selector: 'lib-app-sudoku-board',
  templateUrl: './sudoku-board.component.html',
  styleUrls: ['./sudoku-board.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, SudokuKeyboardComponent],
})
export class SudokuBoardComponent implements OnInit, OnDestroy, AfterContentInit {
  @ViewChild('sudokuBoard', { static: true }) sudokuBoardRef: ElementRef;

  tiles: SudokuTile[][];
  tilesFlat: SudokuTile[];

  rowCount: number;
  colCount: number;

  borderLogicGroups: SudokuTile[][];
  columnLogicGroups: SudokuTile[][];
  rowLogicGroups: SudokuTile[][];

  selectedIndex: number;
  lastSelectedIndex: number;

  highlightValue = 'none';

  userInput: string[] = [];

  transformValue = 'scale(1)';

  boardHeight: number;
  boardWidth: number;

  scaledBoardHeight: number;
  scaledBoardWidth: number;

  initialized = false;

  puzzleID: string;

  exposedCount: number;

  allowedChars: string[];

  borderLogicCols: number;
  borderLogicRows: number;

  enabledInputs: string[] = [];

  boardLayout: SudokuBoardLayout = { puzzleScale: 0 };

  SudokuKeyboardPositions = SudokuKeyboardPositions;
  SudokuKeyboardLayouts = SudokuKeyboardLayouts;
  highlightWrong = false;
  showErrorsImmediately = false;

  private capsLockPressed = false;

  private winDialogSubscription: Subscription;
  private buttonBarSubscription: Subscription;

  private highlightWrongTimeout: number;

  protected notesMode = false;
  private notesModeSubscription: Subscription;

  private helpDialogSubscription: Subscription;
  private paused: number;
  private scaleTimeout: number;
  private finishedGame = false;

  constructor(
    private framework: PuzzleFrameworkService,
    private el: ElementRef,
    private store: Store,
    private translate: TranslateService,
    private cd: ChangeDetectorRef,
    private helpDialogService: PuzzleHelpDialogService,
    private winDialogService: PuzzleWinDialogService,
    private helperFunctions: HelperFunctionsService,
    private keyboardService: PuzzleKeyboardService,
    private settingsModalService: PuzzleSettingsModalService
  ) {
    this.helpDialogSubscription = helpDialogService.helpDialogRegistered.subscribe(() => {
      helpDialogService.injectHelpComponent(SudokuHelpComponent, {});
      this.finishedGame = true;
    });

    this.keyboardService.keyFilterRegex = /[^1-9\s]+/g;
    this.keyboardService.onCapsLock.subscribe((pressed) => {
      this.capsLockChanged(pressed);
      this.cd.detectChanges();
    });
    this.keyboardService.onSpecialKeyDown.subscribe((key) => {
      this.onSpecialKeyDown(key);
      this.cd.detectChanges();
    });
    this.keyboardService.onSpecialKeyUp.subscribe((key) => {
      this.onSpecialKeyUp(key);
      this.cd.detectChanges();
    });
    this.keyboardService.onKeyDown.subscribe((key) => {
      this.onKeyPressed(key);
      this.cd.detectChanges();
    });

    this.winDialogSubscription = winDialogService.winDialogRegistered.subscribe(() => {
      winDialogService.injectComponent(SudokuWinScreenComponent, {});
      winDialogService.showButtons([PUZZLE_WIN_DIALOG_BUTTONS.RESTART_GAME, PUZZLE_WIN_DIALOG_BUTTONS.BACK_TO_GAME]);
    });

    this.buttonBarSubscription = this.framework.buttonPressed.subscribe((button) => {
      switch (button) {
        case Button.Check:
          this.triggerHighlightFields();
          break;
        case Button.Reset:
          this.resetPuzzle();
          break;
        case Button.Joker:
          this.jokerPressed();
          break;
      }
    });

    this.store
      .select((state) => state.puzzleGameState.paused)
      .subscribe((paused: number) => {
        this.paused = paused;
      });

    this.notesModeSubscription = this.store
      .select((state) => state.notesModeState)
      .subscribe((enabled) => {
        this.notesMode = enabled;
      });

    this.framework.puzzleEvents.subscribe((event) => {
      switch (event) {
        case PuzzleFrameworkEvents.SOLVE_CURRENT_PUZZLE:
          this._winGame();
          break;
        case PuzzleFrameworkEvents.SOLVE_CURRENT_PUZZLE_BUT_ONE:
          this._winGame(1);
          break;
        case PuzzleFrameworkEvents.ADD_ALL_NOTES:
          this.addAllNotes();
          break;
      }
    });
  }

  private async _winGame(leaveOut = 0) {
    const randomIndices = this.helperFunctions.randomizeArray(this.tilesFlat.map((tile, index) => index));

    leaveOut += leaveOut > 0 ? this.tilesFlat.slice(0, leaveOut).filter((tile) => tile.exposed).length : 0;

    for (const currentIndex of randomIndices) {
      if (currentIndex < leaveOut) {
        continue;
      }
      const currentTile = this.tilesFlat[currentIndex];
      this.userInput[currentTile.index] = currentTile.text;
      await this.helperFunctions.timeout(20);
      this.cd.detectChanges();
    }
    this.persistUserInput();
    this.updateGameProgress();
    this.checkForWin();
  }

  private triggerHighlightFields() {
    if (this.highlightWrongTimeout) {
      clearTimeout(this.highlightWrongTimeout);
    }
    this.highlightWrong = true;
    this.cd.detectChanges();
    this.highlightWrongTimeout = window.setTimeout(() => {
      this.highlightWrong = false;
      this.cd.detectChanges();
    }, 1000);
  }

  private jokerPressed() {
    if (this.selectedIndex === undefined) {
      this.framework.showTranslatedToast('PUZZLE.snack_bar.joker_button.not_selected', 1500);
    } else if (this.tilesFlat[this.selectedIndex].exposed) {
      this.framework.showTranslatedToast('PUZZLE.snack_bar.joker_button.not_solvable', 1500);
    } else if (this.userInput[this.selectedIndex] === this.tilesFlat[this.selectedIndex].text) {
      this.framework.showTranslatedToast('PUZZLE.snack_bar.joker_button.already_solved', 1500);
    } else {
      if (this.notesMode) {
        this.store
          .dispatch(new DisableNotesMode())
          .toPromise()
          .then(() => {
            window.setTimeout(() => {
              this.onKeyPressed(this.tilesFlat[this.selectedIndex].text);
              this.cd.detectChanges();
            }, 100);
          });
      } else {
        this.onKeyPressed(this.tilesFlat[this.selectedIndex].text);
        this.cd.detectChanges();
      }

      this.store.dispatch(new ConsumeJoker({ puzzleID: this.puzzleID }));
    }
  }

  private resetPuzzle() {
    this.finishedGame = false;
    const jokerCount = this.getJokerCount();

    this.framework.initPuzzleGameState(this.puzzleID, PuzzleGameTypesEnum.SudokuGame, jokerCount);
    const userInput = this.getInitialUserInput(this.tilesFlat);
    const notesInput = this.getInitialNotesInput(this.tilesFlat);
    this.initSudokuPuzzle(this.puzzleID, userInput, notesInput);
    this.initGame();
    this.cd.detectChanges();
  }

  ngOnInit(): void {
    this.initGame();

    const observer = new ResizeObserver((entries) => {
      this.calculateLayout();
    });
    observer.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.winDialogSubscription.unsubscribe();
    this.helpDialogSubscription.unsubscribe();
    this.buttonBarSubscription.unsubscribe();
    this.notesModeSubscription.unsubscribe();
  }

  ngAfterContentInit() {
    this.calculateLayout();
  }

  private getPossibleLayout(
    keyboardLayout: SudokuKeyboardLayouts,
    freeScreenHeight: number,
    freeScreenWidth: number
  ): SudokuBoardLayout {
    let spaceRequirement: { space: number; position: SudokuKeyboardPositions };

    switch (keyboardLayout) {
      case SudokuKeyboardLayouts.FLAT_BOTTOM:
        spaceRequirement = { space: 172, position: SudokuKeyboardPositions.BOTTOM };
        break;
      case SudokuKeyboardLayouts.THREE_COL_RIGHT:
        spaceRequirement = { space: 214, position: SudokuKeyboardPositions.RIGHT };
        break;
      case SudokuKeyboardLayouts.TWO_ROW_BOTTOM:
        spaceRequirement = { space: 118, position: SudokuKeyboardPositions.BOTTOM };
        break;
      case SudokuKeyboardLayouts.DEFAULT_RIGHT:
        spaceRequirement = { space: 282, position: SudokuKeyboardPositions.RIGHT };
        break;
      default:
        spaceRequirement = { space: 214, position: SudokuKeyboardPositions.BOTTOM };
        break;
    }

    const remainingSpace =
      spaceRequirement.position === SudokuKeyboardPositions.BOTTOM
        ? {
            height: freeScreenHeight - spaceRequirement.space,
            width: freeScreenWidth,
          }
        : {
            height: freeScreenHeight,
            width: freeScreenWidth - spaceRequirement.space,
          };

    const puzzleScale = Math.min(1.3, remainingSpace.width / this.boardWidth, remainingSpace.height / this.boardHeight);

    const keyboardPosition = spaceRequirement.position;

    const keyboardSize = spaceRequirement.space;

    return { puzzleScale, keyboardPosition, keyboardLayout, keyboardSize };
  }

  // @HostListener('window:resize')
  calculateLayout() {
    if (document.body.clientWidth === 0) {
      // since the ionic update, inactive browser tabs dont have a body width
      // as long as the tab is active, postpone the zoom operation
      if (this.scaleTimeout) {
        clearTimeout(this.scaleTimeout);
        this.scaleTimeout = undefined;
      }
      this.scaleTimeout = window.setTimeout(() => {
        this.calculateLayout();
      }, 100);
      return;
    }

    const boardMargin = 10;
    const boardMarginSides = this.framework.puzzleFrameworkConfig.removeSideMargins ? 0 : boardMargin;

    const totalHeight = this.el.nativeElement.offsetHeight - boardMargin;
    const totalWidth = this.el.nativeElement.offsetWidth;

    const layoutPriorities = [
      this.getPossibleLayout(SudokuKeyboardLayouts.DEFAULT_BOTTOM, totalHeight, totalWidth - boardMarginSides),
      this.getPossibleLayout(SudokuKeyboardLayouts.DEFAULT_RIGHT, totalHeight, totalWidth - boardMargin),
      this.getPossibleLayout(SudokuKeyboardLayouts.THREE_COL_RIGHT, totalHeight, totalWidth - boardMargin),
      this.getPossibleLayout(SudokuKeyboardLayouts.FLAT_BOTTOM, totalHeight, totalWidth - boardMarginSides),
      this.getPossibleLayout(SudokuKeyboardLayouts.TWO_ROW_BOTTOM, totalHeight, totalWidth - boardMarginSides),
    ];

    this.boardLayout = layoutPriorities.reduce((preferredPriority, currentPriority) => {
      if (!preferredPriority) {
        return currentPriority;
      }
      return preferredPriority.puzzleScale >= currentPriority.puzzleScale ? preferredPriority : currentPriority;
    });

    this.cd.detectChanges();
  }

  highlightLogicGroup(tile: SudokuTile): boolean {
    if (this.selectedIndex === undefined) {
      return false;
    }

    const selectedTile = this.tilesFlat[this.selectedIndex];

    return (
      selectedTile.columnLogicGroup.indexOf(tile) !== -1 ||
      selectedTile.rowLogicGroup.indexOf(tile) !== -1 ||
      selectedTile.borderLogicGroup.indexOf(tile) !== -1
    );
  }

  /**
   * Select or deselect the clicked tile.
   * @param event Event
   * @param tile SudokuTile
   */
  onTileClicked(event: Event, tile: SudokuTile) {
    // event.preventDefault();
    // console.log(tile);
    // Deselect if clicked the same tile.
    if (this.selectedIndex === tile.index) {
      this.deselectUnhighlight();
    } else {
      this.selectedIndex = tile.index;
      this.highlightValue = tile.exposed ? tile.text : this.userInput[tile.index] || 'none';
    }
  }

  /**
   * Deselect the currently selected tile and unhighlight the current value.
   */
  deselectUnhighlight() {
    this.deselectTile();
    this.unhighlightValue();
  }

  /**
   * Unhighlight the current value.
   */
  unhighlightValue() {
    this.highlightValue = 'none';
  }

  /**
   * Deselect the currently selected tile.
   */
  deselectTile() {
    // Store the last index, so arrow keys can recover from unselecting the cursor.
    this.lastSelectedIndex = this.selectedIndex;
    this.selectedIndex = undefined;
  }

  private initGame() {
    this.deselectUnhighlight();

    const puzzleConfig = this.framework.puzzleConfig.puzzle as SudokuPuzzleConfig;
    this.rowCount = parseInt(puzzleConfig._rowCount, 10);
    this.colCount = parseInt(puzzleConfig._colCount, 10);

    this.boardHeight = this.rowCount * 50 + 3;
    this.boardWidth = this.colCount * 50 + 3;

    this.tiles = this.getEmptyTileArray(this.rowCount, this.colCount);

    // console.log(puzzleConfig);
    const puzzleID = puzzleConfig._id;
    this.puzzleID = puzzleID;

    this.exposedCount = 0;
    for (const tile of puzzleConfig.game.tile) {
      const row = parseInt(tile._y, 10);
      const col = parseInt(tile._x, 10);
      this.tiles[row][col].text = tile.__text;

      if (tile._exposed === 'true') {
        this.exposedCount++;
        this.tiles[row][col].exposed = true;
      } else {
        this.tiles[row][col].exposed = false;
      }

      // window.setTimeout(() => {
      //   this.userInput[this.tiles[row][col].index] = this.tiles[row][col].text;
      //   this.persistUserInput();
      //   this.updateGameProgress();
      // }, Math.random() * 3000);
    }

    // collect allowed chars
    const allowedChars = [];
    for (let col = 0; col < this.colCount; col++) {
      const tile = this.tiles[0][col];
      if (allowedChars.indexOf(tile.text) === -1) {
        allowedChars.push(tile.text);
      }
    }
    allowedChars.sort((a, b) => a.localeCompare(b));
    this.allowedChars = allowedChars;

    this.tilesFlat = this.tiles.reduce((acc, val) => acc.concat(val), []);

    this.userInput = this.getInitialUserInput(this.tilesFlat);

    this.initBorderLogicGroups();
    this.initNoBorderLogicGroups();

    this.store.dispatch(new SetPuzzleID({ puzzleID }));

    this.store.select(PuzzleGameState.getByID(puzzleID)).subscribe((puzzleGame) => {
      if (puzzleGame === null) {
        const jokerCount = this.getJokerCount();
        this.framework.initPuzzleGameState(puzzleID, PuzzleGameTypesEnum.SudokuGame, jokerCount);
        return;
      }
    });

    this.store.select(SudokuGameState.getByID(puzzleID)).subscribe((sudokuGame: SudokuGameModel) => {
      if (sudokuGame === null) {
        this.initSudokuPuzzle(puzzleID, this.userInput, this.getInitialNotesInput(this.tilesFlat));
        return;
      }

      this.userInput = JSON.parse(JSON.stringify(sudokuGame.userInput));

      this.tilesFlat.map((tile) => {
        if (!tile.exposed && sudokuGame.notesInput) {
          tile.notes = JSON.parse(JSON.stringify(sudokuGame.notesInput[tile.index]));
        }
      });

      this.updateAllowedChars();
      if (!this.initialized) {
        this.checkAllFieldsLogicGroups();
        this.initialized = true;
        window.setTimeout(() => {
          // this.scalePuzzle();
          // this.cd.detectChanges();
        }, 10);
      }
    });

    // Now subscribe to the showing errors. Needs to be here, so tilesFlat is initialized.
    this.store
      .select((state) => state.puzzleGameState.showErrorsImmediately)
      .subscribe((enabled) => {
        this.showErrorsImmediately = enabled;

        // When the setting changes, check all fields for correctness if enabled,
        // or clear correctness errors if disabled
        if (enabled) {
          this.checkAllFieldsCorrectness();
        } else {
          // Clear correctness errors by removing tiles from their own conflicting tiles
          this.tilesFlat.forEach((tile) => {
            tile.conflictingTiles = tile.conflictingTiles.filter((conflictingTile) => conflictingTile !== tile);
          });
        }

        this.cd.detectChanges();
      });
  }

  private getInitialUserInput(tilesFlat: SudokuTile[]) {
    const userInput: string[] = [];
    tilesFlat.map((tile) => {
      if (tile.exposed) {
        userInput[tile.index] = tile.text;
      }
    });
    return userInput;
  }

  private getInitialNotesInput(tilesFlat: SudokuTile[]) {
    const notesInput: string[][] = [];
    tilesFlat.map((tile) => {
      if (!tile.exposed) {
        notesInput[tile.index] = [];
      }
    });
    return notesInput;
  }

  private checkForWin() {
    for (const tile of this.tilesFlat) {
      // When tile is input field and not filled correct
      if (!tile.exposed && !(this.userInput[tile.index] && this.userInput[tile.index] === tile.text)) {
        return false;
      }
    }
    return true;
  }

  private updateGameProgress() {
    const total = this.tilesFlat.length - this.exposedCount;
    const correct = this.tilesFlat.filter((tile) => !tile.exposed && this.userInput[tile.index] === tile.text).length;
    const puzzleID = this.puzzleID;
    this.store.dispatch([
      new SetGameProgress({
        puzzleID,
        progress: correct / total,
      }),
      new IncMoveCounter({ puzzleID }),
    ]);

    if (this.checkForWin()) {
      this.store.dispatch(new SetFinished({ puzzleID: this.puzzleID }));
    }
  }

  private initSudokuPuzzle(puzzleID: string, userInput: string[], notesInput: string[][]) {
    this.store.dispatch(new InitSudokuGame({ userInput, puzzleID, notesInput }));
  }

  private getEmptyTileArray(rowCount: number, colCount: number): SudokuTile[][] {
    const tiles: SudokuTile[][] = [];
    let index = 0;
    for (let row = 0; row < rowCount; row++) {
      tiles[row] = [];
      for (let column = 0; column < colCount; column++) {
        tiles[row][column] = {
          notes: [],
          index: index++,
          conflictingTiles: [],
        };
      }
    }
    return tiles;
  }

  private persistUserInput() {
    this.store.dispatch(
      new PersistSudokuState({
        userInput: this.userInput,
        puzzleID: this.puzzleID,
        notesInput: this.tilesFlat.map((tile) => (tile.exposed ? null : tile.notes)),
      })
    );
  }

  private initNoBorderLogicGroups() {
    // init horizontally
    const rowGroups = [];
    const colGroups = [];
    for (let row = 0; row < this.rowCount; row++) {
      rowGroups[row] = [];
      for (let col = 0; col < this.colCount; col++) {
        if (row === 0) {
          colGroups[col] = [];
        }
        const tile = this.tiles[row][col];
        rowGroups[row].push(tile);
        colGroups[col].push(tile);
        tile.rowLogicGroup = rowGroups[row];
        tile.columnLogicGroup = colGroups[col];
      }
    }

    this.columnLogicGroups = colGroups;
    this.rowLogicGroups = rowGroups;
  }

  private initBorderLogicGroups() {
    const divisionPossibilities = [];

    const possibleLogicGroups: { rows: number; cols: number }[] = [];

    for (let i = 2; i < this.rowCount; i++) {
      if (this.rowCount % i === 0) {
        divisionPossibilities.push(i);
      }
    }

    for (const possibility of divisionPossibilities) {
      const factor = this.rowCount / possibility;
      if (this.validateLogicGroup(possibility, factor)) {
        possibleLogicGroups.push({
          rows: possibility,
          cols: possibility,
        });
      }
    }
    if (possibleLogicGroups.length === 0) {
      return;
    }

    const logicGroup = possibleLogicGroups.sort((a, b) => Math.abs(a.rows - a.cols) - Math.abs(b.rows - b.cols))[0];

    this.borderLogicGroups = [];

    this.borderLogicCols = logicGroup.cols;
    this.borderLogicRows = logicGroup.rows;

    for (let groupRow = 0; groupRow < logicGroup.cols; groupRow++) {
      for (let groupCol = 0; groupCol < logicGroup.rows; groupCol++) {
        const borderLogicGroup: SudokuTile[] = [];
        this.borderLogicGroups.push(borderLogicGroup);
        for (const index of this.getGroupIndices(groupRow, logicGroup.rows, groupCol, logicGroup.cols)) {
          const tile = this.tiles[index.row][index.col];
          borderLogicGroup.push(tile);
          tile.borderLogicGroup = borderLogicGroup;
          tile.borders = {};
          if (index.borders.indexOf(SudokuBorderTypes.TOP) !== -1) {
            tile.borders['border-top'] = true;
          }
          if (index.borders.indexOf(SudokuBorderTypes.LEFT) !== -1) {
            tile.borders['border-left'] = true;
          }
          if (index.borders.indexOf(SudokuBorderTypes.RIGHT) !== -1) {
            tile.borders['border-right'] = true;
          }
          if (index.borders.indexOf(SudokuBorderTypes.BOTTOM) !== -1) {
            tile.borders['border-bottom'] = true;
          }
        }
      }
    }
  }

  private getGroupIndices(
    groupRow,
    rows,
    groupCol,
    cols
  ): { row: number; col: number; borders: SudokuBorderTypes[] }[] {
    const indices = [];
    const startRowIndex = groupRow * rows;
    const startColIndex = groupCol * cols;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const borders = [];
        if (row === 0) {
          borders.push(SudokuBorderTypes.TOP);
        }
        if (row === rows - 1) {
          borders.push(SudokuBorderTypes.BOTTOM);
        }
        if (col === 0) {
          borders.push(SudokuBorderTypes.LEFT);
        }
        if (col === cols - 1) {
          borders.push(SudokuBorderTypes.RIGHT);
        }
        indices.push({ row: startRowIndex + row, col: startColIndex + col, borders });
      }
    }
    return indices;
  }

  private checkFieldLogicGroups(selectedIndex: number) {
    const tile = this.tilesFlat[selectedIndex];
    this.checkLogicGroup(tile.borderLogicGroup, tile);
    this.checkLogicGroup(tile.columnLogicGroup, tile);
    this.checkLogicGroup(tile.rowLogicGroup, tile);
  }

  private checkFieldCorrectness(selectedIndex: number) {
    const tile = this.tilesFlat[selectedIndex];
    const userInputValue = this.userInput[selectedIndex];

    // If the user input doesn't match the correct value, add the tile to its own conflicting tiles
    if (userInputValue && userInputValue !== tile.text) {
      if (tile.conflictingTiles.indexOf(tile) === -1) {
        tile.conflictingTiles.push(tile);
      }
    } else {
      // Remove the tile from its own conflicting tiles if it's correct or empty
      tile.conflictingTiles = tile.conflictingTiles.filter((conflictingTile) => conflictingTile !== tile);
    }
  }

  private updateAllowedChars() {
    // const allowed = JSON.parse(JSON.stringify(this.allowedChars));

    // console.log(allowed);

    const required = [];

    this.borderLogicGroups.map((group) => {
      group.map((tile) => {
        const inputChar = this.userInput[tile.index];
        if (inputChar !== tile.text && required.indexOf(tile.text) === -1) {
          required.push(tile.text);
        }
      });
    });

    this.enabledInputs = required;
  }

  private checkAllFieldsLogicGroups() {
    this.tilesFlat
      .filter((tile) => tile.exposed === false && this.userInput[tile.index] && this.userInput[tile.index].length !== 0)
      .map((tile) => {
        this.checkFieldLogicGroups(tile.index);
      });
  }

  private checkAllFieldsCorrectness() {
    this.tilesFlat
      .filter((tile) => tile.exposed === false && this.userInput[tile.index] && this.userInput[tile.index].length !== 0)
      .map((tile) => {
        this.checkFieldCorrectness(tile.index);
      });
  }

  private checkLogicGroup(logicGroup: SudokuTile[], tile: SudokuTile) {
    const thisUserInput = this.userInput[tile.index];
    logicGroup.map((otherTile) => {
      if (otherTile === tile) {
        return;
      }
      const otherUserInput = this.userInput[otherTile.index];

      if (otherUserInput === thisUserInput) {
        // Conflict add conflicting tiles
        if (tile.conflictingTiles.indexOf(otherTile) === -1) {
          tile.conflictingTiles.push(otherTile);
        }
        if (otherTile.conflictingTiles.indexOf(tile) === -1) {
          otherTile.conflictingTiles.push(tile);
        }
      } else {
        tile.conflictingTiles = tile.conflictingTiles.filter((conflictingTile) => conflictingTile !== otherTile);
        otherTile.conflictingTiles = otherTile.conflictingTiles.filter((conflictingTile) => conflictingTile !== tile);
      }
    });
  }

  onSpecialKeyDown(key: PuzzleSpecialKeys) {
    if (this.paused) {
      return;
    }

    // Use the currently selected index, or the last one.
    const tempIndex = this.selectedIndex == null ? this.lastSelectedIndex : this.selectedIndex;

    switch (key) {
      case PuzzleSpecialKeys.Escape:
        this.deselectUnhighlight();
        break;
      case PuzzleSpecialKeys.Shift:
        this.toggleNotesMode();
        break;
      case PuzzleSpecialKeys.ArrowDown:
        if (tempIndex !== undefined && tempIndex + this.colCount < this.tilesFlat.length) {
          this.onTileClicked(null, this.tilesFlat[tempIndex + this.colCount]);
        }
        break;
      case PuzzleSpecialKeys.ArrowLeft:
        if (
          tempIndex !== undefined &&
          Math.floor(tempIndex / this.rowCount) === Math.floor((tempIndex - 1) / this.rowCount)
        ) {
          this.onTileClicked(null, this.tilesFlat[tempIndex - 1]);
        }
        break;
      case PuzzleSpecialKeys.ArrowRight:
        if (
          tempIndex !== undefined &&
          Math.floor(tempIndex / this.rowCount) === Math.floor((tempIndex + 1) / this.rowCount)
        ) {
          this.onTileClicked(null, this.tilesFlat[tempIndex + 1]);
        }
        break;
      case PuzzleSpecialKeys.ArrowUp:
        if (tempIndex !== undefined && tempIndex - this.colCount >= 0) {
          this.onTileClicked(null, this.tilesFlat[tempIndex - this.colCount]);
        }
        break;
    }
  }
  onSpecialKeyUp(key: string) {
    switch (key) {
      case 'Shift':
        this.toggleNotesMode();
        break;
    }
  }

  toggleNotesMode() {
    if (this.notesMode) {
      this.store.dispatch(new DisableNotesMode());
    } else {
      this.store.dispatch(new EnableNotesMode());
    }
  }

  capsLockChanged(pressed: boolean) {
    if (this.capsLockPressed === pressed) {
      return;
    }
    this.capsLockPressed = pressed;

    if (this.capsLockPressed) {
      this.store.dispatch(new EnableNotesMode());
    } else {
      this.store.dispatch(new DisableNotesMode());
    }
  }

  disableDeleteButton() {
    // Check if the selected field exists and it is allowed to delete.
    const currentlySelectedAllowedDelete =
      this.selectedIndex != null && this.userInput[this.selectedIndex] && !this.tilesFlat[this.selectedIndex].exposed;
    // Check if the last selected field exists and it is allowed to delete.
    const lastSelectedAllowedDelete =
      this.lastSelectedIndex != null &&
      this.userInput[this.lastSelectedIndex] &&
      !this.tilesFlat[this.lastSelectedIndex].exposed;
    return !currentlySelectedAllowedDelete && !lastSelectedAllowedDelete;
  }

  onKeyPressed(key: string) {
    // console.log(
    //   "key up:",
    //   `key=${key},`,
    //   `this.selectedIndex=${this.selectedIndex},`,
    //   `this.highlightValue=${this.highlightValue},`,
    // );
    // If the game is finished or paused, we don't accept any input.
    if (this.finishedGame || this.paused) {
      return;
    }

    // Space changes the notesMode
    if (key === ' ') {
      // console.log("Space was pressed.");
      if (this.notesMode) {
        // console.log("\tNotes mode was on, disable it.");
        this.store.dispatch(new DisableNotesMode());
      } else {
        // console.log("\tNotes mode was off, enable it.");
        this.store.dispatch(new EnableNotesMode());
      }
      return;
    }

    // Backspace deletes fields other than exposed ones.
    if (key === '') {
      // console.log("Backspace was pressed.");
      if (this.selectedIndex == null) {
        // console.log("\tNothing was selected.");

        if (this.lastSelectedIndex && !this.tilesFlat[this.lastSelectedIndex].exposed) {
          // console.log("\t\tA field was selected before, remove value.");
          this.userInput[this.lastSelectedIndex] = '';
          this.checkFieldLogicGroups(this.lastSelectedIndex);

          // Clear any correctness errors when deleting a value
          if (this.showErrorsImmediately) {
            this.checkFieldCorrectness(this.lastSelectedIndex);
          }

          this.highlightValue = 'none';
          this.persistUserInput();
          this.updateGameProgress();
          // console.log("\t\tRemove highlight.");
          this.highlightValue = 'none';
          // console.log("\t\tSet cursor to last selected field.");
          this.selectedIndex = this.lastSelectedIndex;
          return;
        }

        // console.log("\tRemove highlight.");
        this.highlightValue = 'none';
        return;
      }

      if (this.tilesFlat[this.selectedIndex].exposed) {
        // console.log("\tExposed field was selected, do nothing.");
        return;
      }

      // console.log("\tNon-exposed field was selected, remove value.");
      this.userInput[this.selectedIndex] = '';
      this.checkFieldLogicGroups(this.selectedIndex);

      // Clear any correctness errors when deleting a value
      if (this.showErrorsImmediately) {
        this.checkFieldCorrectness(this.selectedIndex);
      }

      this.highlightValue = 'none';
      this.persistUserInput();
      this.updateGameProgress();

      // Deselect the field, once the value is entered, so we can search for the next by highlighting with a button.
      this.deselectTile();
      return;
    }

    // console.log(`Other than space or backspace was pressed: "${key}".`);

    if (this.selectedIndex && this.tilesFlat[this.selectedIndex].exposed) {
      // console.log("\tAn exposed field was selected.");
      if (this.userInput[this.selectedIndex] === key) {
        // console.log("Same value pressed as selected, do nothing.");
        return;
      }
      // console.log("\t\tDifferent value pressed than selected, Highlighting value.");
      this.deselectTile();
      this.highlightValue = key;
      return;
    }

    // If nothing (null || undefined) is selected, highlight clicked value.
    if (this.selectedIndex == null) {
      // console.log(`\tNothing was selected.`);

      if (this.lastSelectedIndex && this.userInput[this.lastSelectedIndex] === key) {
        // console.log("\t\tA field with the same letter was selected before.");

        if (this.tilesFlat[this.lastSelectedIndex].exposed) {
          // console.log("\t\t\tAn exposed field was selected, highlight value.");
          this.deselectTile();
          this.highlightValue = key;
          return;
        }

        // console.log("\t\t\tremove value.");
        this.userInput[this.lastSelectedIndex] = '';
        this.checkFieldLogicGroups(this.lastSelectedIndex);

        // Clear any correctness errors when deleting a value
        if (this.showErrorsImmediately) {
          this.checkFieldCorrectness(this.lastSelectedIndex);
        }

        this.highlightValue = 'none';
        this.persistUserInput();
        this.updateGameProgress();
        // console.log("\t\tRemove highlight.");
        this.highlightValue = 'none';
        // console.log("\t\tSet cursor to last selected field.");
        this.selectedIndex = this.lastSelectedIndex;
        return;
      }

      if (this.highlightValue === key) {
        // console.log("\t\tWas highlighted before, remove highlight.");
        this.highlightValue = 'none';
      } else {
        // console.log("\t\tHighlight it.");
        this.highlightValue = key.length ? key : 'none';
      }
      return;
    }

    if (this.tilesFlat[this.selectedIndex].exposed) {
      // console.log("\tExposed field was selected.");
      if (this.highlightValue === key) {
        // console.log("\t\tWas highlighted before, remove highlight.");
        this.highlightValue = 'none';
      } else {
        // console.log("\t\tHighlight it.");
        this.highlightValue = key.length ? key : 'none';
      }
      // console.log("\tRemove selection.");
      this.deselectTile();
      return;
    }

    if (this.notesMode) {
      // console.log("Notes mode is on, toggle note.");
      this.addNote(this.selectedIndex, key);
      return;
    }

    if (this.userInput[this.selectedIndex] === key) {
      // console.log("same value as before, removing it");
      key = '';
    }

    // console.log(`\tAdding "${key}".`);
    this.userInput[this.selectedIndex] = key;
    this.checkFieldLogicGroups(this.selectedIndex);

    // Check if the entered digit is correct when showErrorsImmediately is true
    if (this.showErrorsImmediately && key) {
      this.checkFieldCorrectness(this.selectedIndex);
    }

    this.highlightValue = key.length ? key : 'none';
    this.persistUserInput();
    this.updateGameProgress();
    // Deselect the field, once the value is entered, so we can search for the next by highlighting with a button.
    this.deselectTile();
  }

  noteAllowed(tile: SudokuTile, char: string): boolean {
    return (
      tile.borderLogicGroup.map((t) => this.userInput[t.index]).indexOf(char) === -1 &&
      tile.rowLogicGroup.map((t) => this.userInput[t.index]).indexOf(char) === -1 &&
      tile.columnLogicGroup.map((t) => this.userInput[t.index]).indexOf(char) === -1
    );
  }

  /**
   * Add all notes to the board.
   * @private
   */
  private addAllNotes() {
    // console.log("Add all notes");
    for (let i = 0; i < this.tilesFlat.length; i++) {
      for (let v = 1; v <= 9; v++) {
        // console.log(`add note ${value} to field ${i}`);
        this.addNote(i, v.toString(), true);
      }
    }
  }

  /**
   * Add a given note to the field at the given index. Removes them if already there.
   * @param index Which field (in tilesFlat) to add to.
   * @param value Which value to add (1-9).
   * @param force If true, this will not remove existing values.
   * @private
   */
  private addNote(index: number, value: string, force: boolean = false) {
    if (index == null || this.tilesFlat[index].exposed) {
      return;
    }
    const tile = this.tilesFlat[index];

    let shouldPersist = false;

    switch (value) {
      case '':
        // delete field in any case, notes are deleted by pressing them again
        if (this.userInput[tile.index].length) {
          this.userInput[tile.index] = '';
          this.checkFieldLogicGroups(index);
          this.highlightValue = value.length ? value : 'none';
          this.persistUserInput();
          this.updateGameProgress();
          return;
        }

        break;
      case ' ':
        break;
      default:
        // debounce at least one timeoot per tile
        // if (this.noteRemovalTimeout[tile.index]) {
        //   clearTimeout(this.noteRemovalTimeout[tile.index]);
        //   this.noteRemovalTimeout[tile.index] = undefined;
        // }

        if (tile.notes.indexOf(value) === -1 || force) {
          // The note is not there yet, or we are forcing it, so we'll add it. If it is not allowed, well remove it again.
          tile.notes.push(value);
          tile.notes.sort((a, b) => a.localeCompare(b));
          this.highlightValue = value;

          // Check if note is allowed in the field.
          // If it is allowed it should be persisted.
          // If not the note should be removed after a timeout of 1s.
          if (this.noteAllowed(tile, value)) {
            shouldPersist = true;
          } else {
            window.setTimeout(() => {
              // If note is not allowed for the current position, remove note and update view
              const newNotes = tile.notes.filter((note) => note !== value);
              if (newNotes.length !== tile.notes.length) {
                tile.notes = newNotes;
                this.unhighlightValue();
                this.persistUserInput();
                this.cd.detectChanges();
              }
              // index * 10 so they disappear one after the other when add-all-notes added them.
            }, 1000 + index * 10);
          }
        } else {
          // The note is already there, so we remove it instead.
          tile.notes = tile.notes.filter((note) => note !== value);
          this.unhighlightValue();
          shouldPersist = true;
        }
        break;
    }

    if (shouldPersist) {
      this.persistUserInput();
    }
  }

  private validateLogicGroup(rows, columns): boolean {
    for (let groupRow = 0; groupRow < columns; groupRow++) {
      for (let groupCol = 0; groupCol < rows; groupCol++) {
        const groupValues = [];
        for (const index of this.getGroupIndices(groupRow, rows, groupCol, columns)) {
          if (groupValues.indexOf(this.tiles[index.row][index.col].text) === -1) {
            groupValues.push(this.tiles[index.row][index.col].text);
          } else {
            return false;
          }
        }
      }
    }
    return true;
  }

  private getJokerCount() {
    const jokerConfig = this.framework.puzzleFrameworkConfig.jokerButton;
    switch (jokerConfig.unit) {
      case 'percent':
        return Math.ceil((this.tilesFlat.filter((t) => !t.exposed).length * jokerConfig.value) / 100);
      // "absolute" is the default behaviour.
      case 'absolute':
      default:
        return Math.ceil(jokerConfig.value);
    }
  }
}
